import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Image,
  Input,
  Progress,
  RadioGroup,
  Select,
  Spacer,
  Spinner,
  Switch,
  Text,
  Textarea,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Field, useField, useForm } from "react-final-form";

import { useTranslation } from "react-i18next";
import { MdBuild, MdDelete, MdLink } from "react-icons/md";
import styled from "styled-components";
import {
  useGetPresignedPublicS3UrlLazyQuery,
  useGetPresignedS3UrlLazyQuery,
} from "../graphql";
import { Editor as TinyMCE } from "@tinymce/tinymce-react";
import { MediaLibrary } from "../pages/MediaLibrary";

const getColor = (props) => {
  if (props.isDragAccept) {
    return "#00e676";
  }
  if (props.isDragReject) {
    return "#ff1744";
  }
  if (props.isDragActive) {
    return "#2196f3";
  }
  return "#eeeeee";
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
`;

export const AdaptedTextarea = ({ input, meta, ...rest }) => (
  <Textarea {...input} {...rest} isInvalid={meta.error && meta.touched} />
);

export const CheckboxControl = ({ name, value = "", children }) => {
  const {
    input: { checked, ...input },
    meta: { error, touched, invalid },
  } = useField(name, {
    type: "checkbox", // important for RFF to manage the checked prop
  });
  return (
    <FormControl isInvalid={touched && invalid} my={4}>
      <Checkbox {...input} isInvalid={touched && invalid} my={4}>
        {children}
      </Checkbox>
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
};

export const CheckboxArrayControl = ({ name, value, children }) => {
  const {
    input: { checked, ...input },
    meta: { error, touched },
  } = useField(name, {
    type: "checkbox", // important for RFF to manage the checked prop
    value, // important for RFF to manage list of strings
  });
  return (
    <Checkbox {...input} isChecked={checked} isInvalid={error && touched}>
      {children}
    </Checkbox>
  );
};

export const AdaptedRadioGroup = ({ input, meta, label, children }) => (
  <FormControl isInvalid={meta.touched && meta.invalid} my={4}>
    <FormLabel htmlFor={input.name}>{label}</FormLabel>
    <RadioGroup {...input}>{children}</RadioGroup>
    <FormErrorMessage>{meta.error}</FormErrorMessage>
  </FormControl>
);

export const Control = ({ name, ...rest }) => {
  const {
    meta: { error, touched },
  } = useField(name, { subscription: { touched: true, error: true } });
  return <FormControl {...rest} isInvalid={error && touched} />;
};

export const Error = ({ name }) => {
  const {
    meta: { error },
  } = useField(name, { subscription: { error: true } });
  return <FormErrorMessage>{error}</FormErrorMessage>;
};

export const InputControl = ({ type = "text", name, label }) => {
  const { input, meta } = useField(name);
  return (
    <Control name={name} my={4}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Input
        type={type}
        {...input}
        isInvalid={meta.error && meta.touched}
        id={name}
        placeholder={label}
      />
      <Error name={name} />
    </Control>
  );
};

export const MultiLanguageInputControl = ({ name, label }) => (
  <Control name={name} my={4}>
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <Field name={name} component={AdaptedMultiLanguageInput} id={name} />

    <Error name={name} />
  </Control>
);

export const AdaptedMultiLanguageInput = ({ input, meta, ...rest }) => {
  return <MultiLanguageInput {...input} {...rest} />;
};

export const MultiLanguageInput = ({ onChange, value, ...rest }) => {
  const [sq, setSq] = useState(value.sq);
  const [mk, setMk] = useState(value.mk);
  const [en, setEn] = useState(value.en);

  return (
    <Box>
      <Grid templateColumns="repeat(3, 1fr)" gap={6}>
        <Control name={"sq"} my={4}>
          <FormLabel htmlFor={"sq"}>{"Shqip"}</FormLabel>
          <Input
            type="text"
            id={"sq"}
            placeholder={"Shqip"}
            onChange={(e) => {
              setSq(e.target.value);
              onChange({ sq: e.target.value, en, mk });
            }}
            defaultValue={sq}
          />
        </Control>
        <Control name={"en"} my={4}>
          <FormLabel htmlFor={"en"}>{"Anglisht"}</FormLabel>
          <Input
            type="text"
            id={"en"}
            placeholder={"Anglisht"}
            onChange={(e) => {
              setEn(e.target.value);
              onChange({ sq, en: e.target.value, mk });
            }}
            defaultValue={en}
          />
        </Control>
        <Control name={"mk"} my={4}>
          <FormLabel htmlFor={"mk"}>{"Maqedonisht"}</FormLabel>
          <Input
            type="text"
            id={"mk"}
            placeholder={"Maqedonisht"}
            onChange={(e) => {
              setMk(e.target.value);
              onChange({ sq, en, mk: e.target.value });
            }}
            defaultValue={mk}
          />
        </Control>
      </Grid>
    </Box>
  );
};

export const MultiLanguageTextareaControl = ({ name, label }) => (
  <Control name={name} my={4}>
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <Field name={name} component={AdaptedMultiLanguageTextarea} id={name} />

    <Error name={name} />
  </Control>
);

export const AdaptedMultiLanguageTextarea = ({ input, meta, ...rest }) => {
  return <MultiLanguageTextarea {...input} {...rest} />;
};

export const MultiLanguageTextarea = ({ onChange, value }) => {
  const [sq, setSq] = useState(value.sq);
  const [mk, setMk] = useState(value.mk);
  const [en, setEn] = useState(value.en);

  return (
    <Box>
      <Grid templateColumns="repeat(3, 1fr)" gap={6}>
        <Control name={"sq"} my={4}>
          <FormLabel htmlFor={"sq"}>{"Shqip"}</FormLabel>
          <Textarea
            id={"sq"}
            placeholder={"Shqip"}
            onChange={(e) => {
              setSq(e.target.value);
              onChange({ sq: e.target.value, en, mk });
            }}
            defaultValue={sq}
          />
        </Control>
        <Control name={"en"} my={4}>
          <FormLabel htmlFor={"en"}>{"Anglisht"}</FormLabel>
          <Textarea
            id={"en"}
            placeholder={"Anglisht"}
            onChange={(e) => {
              setEn(e.target.value);
              onChange({ sq, en: e.target.value, mk });
            }}
            defaultValue={en}
          />
        </Control>
        <Control name={"mk"} my={4}>
          <FormLabel htmlFor={"mk"}>{"Maqedonisht"}</FormLabel>
          <Textarea
            id={"mk"}
            placeholder={"Maqedonisht"}
            onChange={(e) => {
              setMk(e.target.value);
              onChange({ sq, en, mk: e.target.value });
            }}
            defaultValue={mk}
          />
        </Control>
      </Grid>
    </Box>
  );
};

export const TextareaControl = ({ name, label }) => (
  <Control name={name} my={4}>
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <Field
      name={name}
      component={AdaptedTextarea}
      placeholder={label}
      id={name}
    />
    <Error name={name} />
  </Control>
);

export const PercentComplete = (props) => {
  const form = useForm();
  const numFields = form.getRegisteredFields().length;
  const numErrors = Object.keys(form.getState().errors).length;
  return (
    <Progress
      value={numFields === 0 ? 0 : ((numFields - numErrors) / numFields) * 100}
      {...props}
    />
  );
};

export type Optional<T> = { [P in keyof T]?: T[P] };

const Preview = ({ type, name, url, size, onDelete }) => {
  const { t } = useTranslation();

  return (
    <Flex>
      <Box>
        {type?.startsWith("image") ? (
          <Image borderRadius="10px" boxSize="150px" src={url} />
        ) : (
          <a target="blank" href={url}>
            <Text fontWeight="500">{name || "Shfaq skedarin"}</Text>
          </a>
        )}

        {size ? (
          <Text fontSize="sm" color="grey">
            {t("forms.size")}: {Math.round((size / 1024 / 1024) * 100) / 100} MB
          </Text>
        ) : null}
      </Box>
      <Spacer />
      <Box>
        <Button
          leftIcon={<MdDelete />}
          colorScheme="red"
          variant="solid"
          onClick={onDelete}
        >
          {t("forms.delete")}
        </Button>
      </Box>
    </Flex>
  );
};

function Dropzone({ onChange, value, ...rest }) {
  const { t } = useTranslation();
  const [clearCurrent, setClearCurrent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [getPresignedUrl, { data }] = useGetPresignedS3UrlLazyQuery();

  const { getPresignedS3Url } = data || {};
  const { url, key } = getPresignedS3Url || {};

  useEffect(() => {
    async function run() {
      if (url) {
        setUploading(() => true);

        await fetch(url, {
          method: "PUT",
          body: files[0],
        });

        if (onChange) {
          setUploading(() => false);
          onChange({ key: key });
        }
      }
    }

    run();

    // eslint-disable-next-line
  }, [url]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    noDrag: false,
    onDrop: async (acceptedFiles) => {
      const files = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );

      setFiles(files);

      getPresignedUrl({
        variables: {
          fileName: files[0].name,
        },
      });
    },
  });

  const removeFile = (file) => () => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
  };

  const thumbs = files.map((file) => (
    <div key={file.name}>
      <div>
        <Box bg="white" w="100%" p={4} borderRadius="5px">
          {uploading ? (
            <>
              <Spinner /> {t("forms.uploading")}
            </>
          ) : (
            <Preview
              type={file.type}
              name={file.name}
              url={file.preview}
              size={file.size}
              onDelete={() => removeFile(file)}
            />
          )}
        </Box>
      </div>
    </div>
  ));

  useEffect(() => {
    if (value.key) {
      onChange({
        key: value.key,
        url: value.url,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    },
    [files]
  );

  const inEditMode = !clearCurrent && value.url;

  return (
    <>
      {!thumbs.length && !inEditMode && (
        <Container
          {...getRootProps({ className: "btn-dropzone" })}
          style={{ background: isDragActive ? "#DDEFFF" : "" }}
        >
          <input {...getInputProps()} />
          <span>{t("forms.slideOrClick")}</span>
        </Container>
      )}
      {inEditMode && (
        <Preview
          name={""}
          onDelete={() => setClearCurrent(true)}
          url={value.url}
          size={""}
          type={value.contentType}
        />
      )}
      <aside>{thumbs}</aside>
    </>
  );
}

function DropzonePublic({ onChange }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [getPresignedUrl, { data }] = useGetPresignedPublicS3UrlLazyQuery();

  const { getPresignedPublicS3Url } = data || {};
  const { url, key } = getPresignedPublicS3Url || {};

  useEffect(() => {
    async function run() {
      if (url) {
        setUploading(() => true);

        await fetch(url, {
          method: "PUT",
          body: files[0],
        });

        if (onChange) {
          setUploading(() => false);
          onChange({ key: key });
        }
      }
    }

    run();

    // eslint-disable-next-line
  }, [url]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    multiple: false,
    noDrag: false,
    onDrop: async (acceptedFiles) => {
      const files = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );

      setFiles(files);

      getPresignedUrl({
        variables: {
          fileName: files[0].name,
        },
      });
    },
  });

  const removeFile = (file) => () => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
  };

  const thumbs = files.map((file) => (
    <div key={file.name}>
      <div>
        <Box bg="white" w="100%" p={4} borderRadius="5px">
          {uploading ? (
            <>
              <Spinner /> {t("forms.uploading")}
            </>
          ) : (
            <Flex>
              <Box>
                {!file.type.startsWith("image") ? (
                  <Text fontWeight="500">{file.name}</Text>
                ) : (
                  <Image
                    borderRadius="10px"
                    boxSize="150px"
                    src={file.preview}
                  />
                )}

                <Text fontSize="sm" color="grey">
                  {t("forms.size")}:{" "}
                  {Math.round((file.size / 1024 / 1024) * 100) / 100} MB
                </Text>
              </Box>
              <Spacer />
              <Box>
                <Button
                  leftIcon={<MdDelete />}
                  colorScheme="red"
                  variant="solid"
                  onClick={removeFile(file)}
                >
                  {t("forms.delete")}
                </Button>
              </Box>
            </Flex>
          )}
        </Box>
      </div>
    </div>
  ));

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    },
    [files]
  );

  return (
    <>
      {!thumbs.length ? (
        <Container
          {...getRootProps({ className: "btn-dropzone" })}
          style={{ background: isDragActive ? "#DDEFFF" : "" }}
        >
          <input {...getInputProps()} />
          <span>{t("forms.slideOrClick")}</span>
        </Container>
      ) : null}
      <aside>{thumbs}</aside>
    </>
  );
}

export const AdaptedDropzone = ({ input, meta, ...rest }) => {
  return rest.unauthenticated ? (
    <DropzonePublic {...input} {...rest} />
  ) : (
    <Dropzone {...input} {...rest} />
  );
};

export const DropzoneControl = ({ name, label, unauthenticated = false }) => (
  <Control name={name} my={4}>
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <Field
      name={name}
      component={AdaptedDropzone}
      id={name}
      unauthenticated={unauthenticated}
    />

    <Error name={name} />
  </Control>
);

export const AdaptedSelect = ({ input, meta, label, children, ...rest }) => {
  return (
    <FormControl isInvalid={meta.touched && meta.invalid} my={4}>
      <FormLabel htmlFor={input.name}>{label}</FormLabel>
      <Select {...input} placeholder="Zgjidh" {...rest}>
        {children}
      </Select>
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

function Timetable({ onChange, value, ...rest }) {
  const [startHour, setStartHour] = useState(value.startHour || 0);
  const [startMinute, setStartMinute] = useState(value.startMinute || 0);
  const [endHour, setEndHour] = useState(value.endHour || 0);
  const [endMinute, setEndMinute] = useState(value.endMinute || 0);

  const isOff = !startHour && !startMinute && !endHour && !endMinute;

  const [isDisabled, setDisabled] = useState(isOff);

  useEffect(() => {
    onChange({ startHour, startMinute, endHour, endMinute });
    // eslint-disable-next-line
  }, [endHour, endMinute, startHour, startMinute]);

  return (
    <>
      <FormControl display="flex" alignItems="center" marginBottom="10px">
        <FormLabel
          htmlFor="email-alerts"
          mb="0"
          fontSize="sm"
          fontWeight="normal"
        >
          Ditë pushim
        </FormLabel>
        <Switch
          id="email-alerts"
          isChecked={isDisabled}
          onChange={() => {
            setDisabled(!isDisabled);

            setStartHour(0);
            setStartMinute(0);
            setEndHour(0);
            setEndMinute(0);
          }}
        />
      </FormControl>
      <Flex>
        <Center w="100px">Nga</Center>
        <Box w="100px">
          <Select
            placeholder="Ora"
            onChange={(event) => setStartHour(parseInt(event.target.value))}
            disabled={isDisabled}
            value={startHour}
          >
            {Array.from({ length: 24 }).map((_, i) => {
              return <option value={`${i}`}>{i}</option>;
            })}
          </Select>
        </Box>
        <Box w="100px">
          <Select
            placeholder="Minuta"
            onChange={(event) => setStartMinute(parseInt(event.target.value))}
            disabled={isDisabled}
            value={startMinute}
          >
            {Array.from({ length: 60 }).map((_, i) => {
              return <option value={`${i}`}>{i}</option>;
            })}
          </Select>
        </Box>
        <Center w="100px"> deri më</Center>
        <Box w="100px">
          <Select
            placeholder="Ora"
            onChange={(event) => setEndHour(parseInt(event.target.value))}
            disabled={isDisabled}
            value={endHour}
          >
            {Array.from({ length: 24 }).map((_, i) => {
              return <option value={`${i}`}>{i}</option>;
            })}
          </Select>
        </Box>
        <Box w="100px">
          <Select
            placeholder="Minuta"
            onChange={(event) => setEndMinute(parseInt(event.target.value))}
            disabled={isDisabled}
            value={endMinute}
          >
            {Array.from({ length: 60 }).map((_, i) => {
              return <option value={`${i}`}>{i}</option>;
            })}
          </Select>
        </Box>

        <Box flex="1"></Box>
      </Flex>
    </>
  );
}

export const AdaptedTimetable = ({ input, meta, ...rest }) => {
  return <Timetable {...input} {...rest} />;
};

export const TimetableControl = ({ name, label, unauthenticated = false }) => (
  <Control name={name} my={4}>
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <Field name={name} component={AdaptedTimetable} id={name} />

    <Error name={name} />
  </Control>
);

export const AdaptedCheckboxes = ({
  input,
  meta,
  label,
  options,
  onChange,
}: // ...rest
any) => {
  return (
    <FormControl isInvalid={meta.touched && meta.invalid} my={4}>
      <FormLabel htmlFor={input.name}>{label}</FormLabel>
      {options.map((o: { label: string; value: string }) => {
        return (
          <div
            key={`list-${input.name}-option-${o.value}`}
            className="custom-control custom-checkbox custom-control-inline"
          >
            <Field
              name={input.name}
              component="input"
              type="checkbox"
              value={o.value}
              id={`list-${input.name}-option-${o.value}`}
            />
            <label
              style={{ padding: "10px", paddingLeft: "10px" }}
              className="custom-control-label"
              htmlFor={`list-${input.name}-option-${o.value}`}
            >
              {o.label}
            </label>
          </div>
        );
      })}
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

export const EditorControl = ({ name, label }) => (
  <Control name={name} my={4}>
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <Field name={name} component={AdaptedEditor} id={name} />

    <Error name={name} />
  </Control>
);

export const AdaptedEditor = ({ input, meta, ...rest }) => {
  return <Editor {...input} {...rest} />;
};

export const Editor = ({ onChange, value }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={onClose}
        size="3xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <MediaLibrary forceHeight={800} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <TinyMCE
        value={value}
        onEditorChange={onChange}
        apiKey="9xwz5zqjglpu8ai12cy8h9viixvk8x2mum8b6im4bwod613g"
        init={{
          height: 500,
          plugins:
            "print preview importcss tinydrive searchreplace autolink autosave save directionality visualblocks visualchars fullscreen image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount imagetools textpattern noneditable help charmap quickbars emoticons",
          mobile: {
            plugins:
              "print preview importcss tinydrive searchreplace autolink autosave save directionality visualblocks visualchars fullscreen image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount textpattern noneditable help charmap quickbars emoticons",
          },
          menubar: "file edit view insert format tools table",
          toolbar:
            "undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor | image media medialibrary link | preview fullscreen | header ",
          setup: function (editor) {
            /* example, adding a toolbar menu button */
            editor.ui.registry.addButton("medialibrary", {
              text: "Galeria",
              onAction: function (_) {
                onOpen();
              },
            });
          },
          autosave_ask_before_unload: true,
          autosave_interval: "30s",
          autosave_restore_when_empty: false,
          autosave_retention: "2m",
          // quickbars_selection_toolbar:
          //   "bold italic | quicklink h2 h3 blockquote quickimage quicktable",
          toolbar_mode: "sliding",
          contextmenu: "link image imagetools table",
          a11y_advanced_options: true,
          skin: "oxide",
          content_css: "default",
          content_style:
            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }",
        }}
      />
    </>
  );
};
